import { useState } from "react";
import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { useToast } from "@/shared/hooks/use-toast";
import { Spinner } from "@/shared/components/ui/spinner";
import {
  UserService,
  User,
  Workspace,
} from "@/features/Users/services/userService";
import { WorkspaceService } from "@/features/Workspaces/services/workspaceService";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { logger } from "@/infrastructure/lib/logger";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { OrganizationUsersTable } from "@/features/Organization/components/OrganizationUsersTable";
import { EditUserWorkspacesDialog } from "@/features/Users/components/EditUserWorkspacesDialog";
import { DeleteUserDialog } from "@/features/Users/components/DeleteUserDialog";
import { UsersSearchBar } from "@/features/Users/components/UsersSearchBar";
import { AddWorkspaceUsersDialog } from "@/features/Workspaces/components/AddWorkspaceUsersDialog";
import { WorkspaceUsersService } from "@/features/Workspaces/services/workspaceUsersService";
import { analyticsService } from "@/shared/services/analytics";
import { useTranslation } from "react-i18next";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { Button } from "@/shared/components/ui/button";
import { UserPlus, X } from "lucide-react";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { InviteService, Invite } from "@/features/Users/services/inviteService";
import { Badge } from "@/shared/components/ui/badge";
import { Avatar, AvatarFallback } from "@/shared/components/ui/avatar";
import { Clock } from "lucide-react";

interface ProductUsersTabProps {
  workspaceId: string;
}

export function WorkspaceUsersTab({ workspaceId }: ProductUsersTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  // Segmento das queryKeys abaixo: sem o id da organização ativa, os dados da
  // organização anterior continuariam em cache após a troca de organização.
  const orgKeySegment = selectedOrganization?.id;
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const { isAdmin } = useIsAdmin();
  const cancelInviteMutation = useMutation({
    mutationFn: (inviteId: string) => InviteService.deleteInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === "workspace-invites",
      });
      queryClient.invalidateQueries({ queryKey: ["invites"] });
      toast({
        title: t("settings.users.inviteCancelSuccess"),
        description: t("settings.users.inviteCancelSuccessDescription"),
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("settings.users.inviteCancelErrorDescription"),
      });
    },
  });

  // Buscar produtos da organização
  const { data: organizationProducts = [] } = useQuery<Workspace[]>({
    queryKey: ["organization-products", orgKeySegment],
    queryFn: async () => {
      try {
        return await WorkspaceService.getCurrentOrganizationProducts();
      } catch (error) {
        logger.error("Failed to fetch organization products", { error });
        throw error;
      }
    },
    staleTime: 60000,
    retry: 1,
  });

  // Buscar invites pendentes filtrados por produto
  const { data: productInvites = [] } = useQuery<Invite[]>({
    queryKey: ["workspace-invites", workspaceId, orgKeySegment],
    queryFn: async () => {
      const allInvites = await InviteService.listInvites();
      return allInvites.filter(
        (inv: Invite) =>
          inv.status === "pending" &&
          Array.isArray(inv.product_ids) &&
          inv.product_ids.includes(workspaceId),
      );
    },
    staleTime: 0,
    refetchOnMount: "always",
    retry: 1,
  });

  // Membros do workspace direto da membership (v2) — inclui superadmins vinculados.
  const {
    data: workspaceMembers = [],
    isLoading: isLoadingMembers,
    error: membersError,
  } = useQuery<User[]>({
    queryKey: ["workspace-members", workspaceId, orgKeySegment],
    queryFn: async () => {
      try {
        return await UserService.getWorkspaceMembers(workspaceId);
      } catch (error) {
        logger.error("Failed to fetch workspace members", { error });
        throw error;
      }
    },
    enabled: true,
    staleTime: 60000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Server-side search of users assigned to this workspace by name/email.
  const {
    data: searchResults,
    isLoading: isSearchingUsers,
    error: searchUsersError,
  } = useQuery({
    queryKey: ["workspace-users-search", workspaceId, searchTerm],
    queryFn: async () => {
      if (!searchTerm) return null;
      const response = await UserService.searchWorkspaceUsers(
        workspaceId,
        searchTerm,
        { limit: 50 },
      );
      analyticsService.trackEvent("users_searched", {
        scope: "workspace",
        product_id: workspaceId,
        query_length: searchTerm.length,
        results_count: response.users.length,
      });
      return response;
    },
    enabled: !!searchTerm,
  });

  // Fonte default: no v2, a membership do workspace (já são os membros); no legacy,
  // a lista da org filtrada por quem tem acesso. Busca ativa sobrepõe com o
  // resultado server-side (já escopado ao workspace).
  const defaultUsers: User[] = workspaceMembers;
  const isLoadingDefault = isLoadingMembers;
  const defaultError = membersError;
  const baseUsers: User[] = searchTerm
    ? (searchResults?.users ?? [])
    : defaultUsers;

  // Buscar produtos para cada usuário
  const userProductsQueries = useQueries({
    queries: (baseUsers || []).map((user) => ({
      queryKey: ["user-products", user.id],
      queryFn: async () => {
        try {
          return await UserService.getUserProducts(user.id);
        } catch (error) {
          logger.error("Failed to fetch user products", {
            userId: user.id,
            error,
          });
          return [];
        }
      },
      enabled: !!user?.id,
    })),
  });

  // Combinar usuários com seus produtos. A fonte já É a membership do workspace,
  // então todos os usuários listados pertencem a ele — sem filtro por workspaceId.
  const usersWithProducts = baseUsers.map((user, index) => {
    const productsQuery = userProductsQueries[index];
    const userProducts = productsQuery?.data || [];
    return {
      ...user,
      products: userProducts,
      productsLoading: productsQuery?.isLoading || false,
    };
  });

  const unlinkUserMutation = useMutation({
    mutationFn: (userId: string) =>
      WorkspaceUsersService.unassignUser(workspaceId, userId),
    onSuccess: () => {
      // A lista desta aba vem de workspace-members (+ a busca server-side).
      // `organization-users` é a lista de outra tela, que este mesmo evento
      // desatualiza: invalidada de leve, sem refetch — quem estiver montado
      // busca de novo, quem não estiver não gera requisição.
      queryClient.invalidateQueries({
        queryKey: ["organization-users", orgKeySegment],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-members", workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-users-search", workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["user-products", selectedUser?.id],
      });
      setUnlinkDialogOpen(false);
      toast({
        title: t("settings.users.unlinkUserSuccess"),
        description: t("settings.users.unlinkUserSuccessDescription"),
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : undefined;
      logger.error("Failed to unlink user from workspace", { error: message });
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: resolveApiErrorMessage(error, t),
      });
    },
  });

  // Mutação para desativar usuário
  const deactivateUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await UserService.deactivateUser(userId);
    },
    onSuccess: () => {
      // Mesma regra do unlink acima.
      queryClient.invalidateQueries({
        queryKey: ["organization-users", orgKeySegment],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-members", workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-users-search", workspaceId],
      });
      setDeleteDialogOpen(false);
      toast({
        title: t("settings.users.deleteUserSuccess"),
        description: t("settings.users.deleteUserSuccessDescription"),
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : undefined;
      logger.error("Failed to deactivate user", { error: message });
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: resolveApiErrorMessage(error, t),
      });
    },
  });

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleUnlinkUser = (user: User) => {
    setSelectedUser(user);
    setUnlinkDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedUser) {
      await deactivateUserMutation.mutateAsync(selectedUser.id);
    }
  };

  const handleConfirmUnlink = async () => {
    if (selectedUser) {
      await unlinkUserMutation.mutateAsync(selectedUser.id);
    }
  };

  const handleSearchSubmit = (term: string) => {
    setSearchTerm(term);
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setSearchTerm(null);
  };

  return (
    <>
      <Card className="w-full">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t("workspaces.users.title")}</CardTitle>
            <CardDescription>
              {t("workspaces.users.description")}
            </CardDescription>
          </div>
          {isAdmin && (
            <Button onClick={() => setInviteDialogOpen(true)} size="sm">
              <UserPlus className="h-4 w-4 mr-2" />
              {t("settings.workspaces.addUsersButton")}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invites pendentes para este produto */}
          {productInvites.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-medium">
                  {t("workspaces.users.pendingInvites")}
                </h3>
                <Badge variant="secondary">{productInvites.length}</Badge>
              </div>
              <div className="border rounded-md p-3 bg-muted/30 space-y-3">
                {productInvites.map((invite, index) => (
                  <div key={invite.id}>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 flex-1">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs">
                            {(invite.user_name ||
                              invite.email)?.[0]?.toUpperCase() || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-medium">
                            {invite.user_name || "-"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {invite.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {t("settings.users.awaitingAccess")}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          aria-label={t("settings.users.cancelInvite")}
                          onClick={() => cancelInviteMutation.mutate(invite.id)}
                          disabled={cancelInviteMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {index < productInvites.length - 1 && (
                      <div className="border-t mt-3" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Barra de busca de usuários do workspace */}
          <UsersSearchBar
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={handleSearchSubmit}
            onClear={handleSearchClear}
            isSearching={isSearchingUsers}
            isActive={!!searchTerm}
          />

          {/* Usuários vinculados */}
          {(isLoadingDefault && !searchTerm) || isSearchingUsers ? (
            <div className="flex flex-col items-center justify-center p-8 space-y-4">
              <Spinner className="h-8 w-8" />
              <p className="text-sm text-muted-foreground">
                {t("common.loading")}
              </p>
            </div>
          ) : (defaultError && !searchTerm) || searchUsersError ? (
            <div className="p-4 border rounded-md bg-destructive/10">
              <p className="text-sm text-destructive">
                {t("common.errorLoadingData")}
              </p>
            </div>
          ) : usersWithProducts.length > 0 ? (
            <div className="border rounded-md overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b bg-muted/30">
                <UserPlus className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-medium">
                  {t("workspaces.users.activeUsers")}
                </h3>
                <Badge variant="secondary">{usersWithProducts.length}</Badge>
              </div>
              <div className="p-4">
                <OrganizationUsersTable
                  users={usersWithProducts}
                  onEditUser={handleEditUser}
                  onDeleteUser={handleDeleteUser}
                  onUnlinkUser={handleUnlinkUser}
                  currentUserId={currentUser?.id}
                />
              </div>
            </div>
          ) : searchTerm ? (
            <div className="p-4 border rounded-md">
              <p className="text-sm text-muted-foreground">
                {t("settings.users.search.noResults")}
              </p>
            </div>
          ) : (
            <div className="p-4 border rounded-md">
              <p className="text-sm text-muted-foreground">
                {t("users.noOrganizationUsers", {
                  defaultValue: "No users found",
                })}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <AddWorkspaceUsersDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        workspaceId={workspaceId}
      />

      {selectedUser && (
        <>
          <EditUserWorkspacesDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            user={selectedUser}
            organizationProducts={organizationProducts}
          />
          <DeleteUserDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            user={selectedUser}
            onConfirm={handleConfirmDelete}
            isDeleting={deactivateUserMutation.isPending}
          />
          <DeleteUserDialog
            mode="unlink"
            open={unlinkDialogOpen}
            onOpenChange={setUnlinkDialogOpen}
            user={selectedUser}
            onConfirm={handleConfirmUnlink}
            isDeleting={unlinkUserMutation.isPending}
          />
        </>
      )}
    </>
  );
}
